/**
 * Helper functions for form submission handling with question_id support
 */

/**
 * Convert label-based answers to question_id-based answers
 * @param {Object} labelBasedAnswers - Answers object with labels as keys: { "Student ID": "12345" }
 * @param {Array} questions - Form questions array with question_id
 * @returns {Object} - Answers object with question_id as keys: { "uuid-123": "12345" }
 */
export const convertAnswersToQuestionIdFormat = (labelBasedAnswers, questions) => {
  if (!labelBasedAnswers || !questions || !Array.isArray(questions)) {
    return {};
  }

  const questionIdAnswers = {};
  const labelToQuestionIdMap = new Map(
    questions.map(q => [q.label, q.question_id]).filter(([label, id]) => label && id)
  );

  Object.entries(labelBasedAnswers).forEach(([label, value]) => {
    const questionId = labelToQuestionIdMap.get(label);
    if (questionId) {
      questionIdAnswers[questionId] = value;
    } else {
      // If label not found, try to match by checking if it's already a question_id
      // (for backward compatibility with mixed formats)
      if (questions.some(q => q.question_id === label)) {
        questionIdAnswers[label] = value;
      }
    }
  });

  return questionIdAnswers;
};

/**
 * Convert question_id-based answers to label-based answers (for display/backward compatibility)
 * @param {Object} questionIdAnswers - Answers object with question_id as keys: { "uuid-123": "12345" }
 * @param {Array} questions - Form questions array with question_id
 * @returns {Object} - Answers object with labels as keys: { "Student ID": "12345" }
 */
export const convertAnswersToLabelFormat = (questionIdAnswers, questions) => {
  if (!questionIdAnswers || !questions || !Array.isArray(questions)) {
    return {};
  }

  const labelAnswers = {};
  const questionIdToLabelMap = new Map(
    questions.map(q => [q.question_id, q.label]).filter(([id, label]) => id && label)
  );

  Object.entries(questionIdAnswers).forEach(([key, value]) => {
    // Check if key is a question_id
    const label = questionIdToLabelMap.get(key);
    if (label) {
      labelAnswers[label] = value;
    } else {
      // If not found in map, might be label-based already (backward compatibility)
      // Check if it matches a label directly
      if (questions.some(q => q.label === key)) {
        labelAnswers[key] = value;
      }
    }
  });

  return labelAnswers;
};

/**
 * Detect if answers are in label-based format or question_id-based format
 * @param {Object} answers - Answers object
 * @param {Array} questions - Form questions array
 * @returns {string} - 'question_id' | 'label' | 'mixed'
 */
export const detectAnswerFormat = (answers, questions) => {
  if (!answers || !questions || !Array.isArray(questions)) {
    return 'unknown';
  }

  const questionIds = new Set(questions.map(q => q.question_id).filter(Boolean));
  const labels = new Set(questions.map(q => q.label).filter(Boolean));
  
  const answerKeys = Object.keys(answers);
  if (answerKeys.length === 0) return 'empty';

  const hasQuestionIds = answerKeys.some(key => questionIds.has(key));
  const hasLabels = answerKeys.some(key => labels.has(key));

  if (hasQuestionIds && hasLabels) return 'mixed';
  if (hasQuestionIds) return 'question_id';
  if (hasLabels) return 'label';
  
  return 'unknown';
};

/**
 * Get answer value by question label (for display purposes)
 * Assumes answers are in question_id format
 * @param {Object} answers - Answers object with question_id as keys
 * @param {Array} questions - Form questions array
 * @param {string} label - Question label to look up
 * @returns {any} - Answer value or undefined
 */
export const getAnswerByLabel = (answers, questions, label) => {
  if (!answers || !questions || !label) return undefined;
  
  const question = questions.find(q => q.label === label && q.question_id);
  if (!question) return undefined;
  
  return answers[question.question_id];
};

/**
 * Get default initial answers using question_id as keys
 * @param {Object} form - Form object with questions array
 * @returns {Object} - Default answers with question_id as keys
 */
export const getDefaultInitialAnswers = (form) => {
  try {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const today = `${yyyy}-${mm}-${dd}`;
    
    const defaults = {};
    if (form && Array.isArray(form.questions)) {
      const dateField = form.questions.find(q => 
        String(q.label).toLowerCase() === 'date' && q.question_id
      );
      if (dateField && dateField.question_id) {
        defaults[dateField.question_id] = today;
      }
    }
    return defaults;
  } catch (_e) {
    return {};
  }
};
